import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LogOut, Lock, CreditCard, Activity, Calendar, User, Key } from 'lucide-react';

const MemberDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [otp, setOtp] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [loading, setLoading] = useState({});
  const [sharedCode, setSharedCode] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchSharedCode();
  }, []);

  const fetchSharedCode = async () => {
    try {
      const response = await api.get('/access/shared-code');
      if (response.data.has_code) {
        setSharedCode(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch shared code');
    }
  };

  const fetchDashboardData = async () => {
    try {
      const results = await Promise.allSettled([
        api.get('/dashboard/stats'),
        api.get('/payments/history')
      ]);
      
      const getData = (result, fallback) => result.status === 'fulfilled' ? result.value.data : fallback;
      
      setStats(getData(results[0], { subscription_status: 'inactive', subscription_plan: null, days_remaining: 0, total_visits: 0 }));
      setPayments(getData(results[1], []));
      
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length > 0) {
        console.error('Dashboard fetch errors:', failed.map(r => r.reason?.message));
        if (failed.length === results.length) {
          toast.error('Failed to load dashboard data');
        }
      }
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Failed to load dashboard data');
      setStats({ subscription_status: 'inactive', subscription_plan: null, days_remaining: 0, total_visits: 0 });
      setPayments([]);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const generateOTP = async () => {
    setLoading({ ...loading, otp: true });
    try {
      const response = await api.post('/access/generate-otp');
      setOtp(response.data);
      toast.success('OTP sent to your email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate OTP');
    } finally {
      setLoading({ ...loading, otp: false });
    }
  };

  const generateTempPassword = async () => {
    setLoading({ ...loading, temp: true });
    try {
      const response = await api.post('/access/generate-temp-password');
      setTempPassword(response.data);
      toast.success('Temporary password sent to your email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate password');
    } finally {
      setLoading({ ...loading, temp: false });
    }
  };

  if (!stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6 md:p-12">
      <div className="container mx-auto max-w-7xl">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-heading font-black uppercase tracking-tight" data-testid="dashboard-title">
            Member Dashboard
          </h1>
          <div className="flex gap-3">
            <Link to="/profile">
              <Button
                variant="outline"
                className="border-secondary text-secondary hover:bg-secondary hover:text-black"
                data-testid="profile-btn"
              >
                <User className="w-4 h-4 mr-2" />
                Profile
              </Button>
            </Link>
            <Link to="/">
              <Button
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-white"
                data-testid="back-home-btn"
              >
                Back to Home
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="border-destructive text-destructive hover:bg-destructive hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Shared Daily Access Code */}
        {sharedCode?.has_code && (
          <Card className="bg-gradient-to-r from-green-500/20 to-blue-500/20 border-green-500/30 mb-8">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-500/20 rounded-full">
                    <Key className="w-8 h-8 text-green-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-green-400">Today's Access Code</h3>
                    <p className="text-sm text-muted-foreground">{sharedCode.message || 'Use this code to access the gym'}</p>
                  </div>
                </div>
                <div className="bg-card/80 border border-green-500/50 rounded-xl px-8 py-4 text-center">
                  <p className="text-4xl md:text-5xl font-mono font-bold text-green-400 tracking-[0.3em]" data-testid="shared-access-code">
                    {sharedCode.code}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Valid until: {new Date(sharedCode.expires_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Subscription Status */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-card/50 border-white/10 col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Activity className="w-5 h-5 mr-2 text-primary" />
                Subscription Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-2xl font-bold" data-testid="subscription-status">
                  {stats?.subscription_status === 'active' ? (
                    <span className="text-green-500">ACTIVE</span>
                  ) : stats?.subscription_status === 'paused' ? (
                    <span className="text-yellow-500">PAUSED</span>
                  ) : stats?.subscription_status === 'cancelled' ? (
                    <span className="text-red-500">CANCELLED</span>
                  ) : stats?.subscription_status === 'expired' ? (
                    <span className="text-red-500">EXPIRED</span>
                  ) : (
                    <span className="text-yellow-500">INACTIVE</span>
                  )}
                </p>
                {stats?.subscription_plan && (
                  <p className="text-muted-foreground">Plan: {stats.subscription_plan}</p>
                )}
                {stats?.session_type && (
                  <p className="text-muted-foreground">Session: {stats.session_type}</p>
                )}
                
                {/* Countdown Timer for Active Subscription */}
                {stats?.subscription_status === 'active' && stats?.days_remaining !== undefined && (
                  <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-sm text-green-400 font-semibold mb-1">Time Remaining:</p>
                    <p className="text-2xl font-bold text-green-500">
                      {stats.days_remaining > 0 && `${stats.days_remaining}d `}
                      {stats.hours_remaining}h {stats.minutes_remaining}m
                    </p>
                    {stats?.expiry_date && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Expires: {new Date(stats.expiry_date).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
                
                {/* Expired Warning */}
                {stats?.subscription_status === 'expired' && (
                  <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-400 font-semibold">Your subscription has expired!</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Please renew to regain access to the gym.
                    </p>
                  </div>
                )}
                
                {/* Subscribe/Renew Button */}
                {(stats?.subscription_status === 'inactive' || stats?.subscription_status === 'cancelled' || stats?.subscription_status === 'expired' || !stats?.subscription_status) && (
                  <div className="mt-4">
                    <Link to="/plans">
                      <Button className="bg-primary" data-testid="subscribe-now-btn">
                        {stats?.subscription_status === 'expired' ? 'Renew Subscription' : 'Subscribe Now'}
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-secondary" />
                Total Visits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold" data-testid="total-visits">{stats?.total_visits || 0}</p>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center">
                <CreditCard className="w-5 h-5 mr-2 text-primary" />
                Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold" data-testid="payment-count">{payments?.length || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link to="/fitness">
            <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30 hover:border-primary transition-all cursor-pointer h-full">
              <CardContent className="pt-6">
                <Activity className="w-8 h-8 text-primary mb-2" />
                <p className="font-heading font-bold uppercase text-lg">Fitness Tracker</p>
                <p className="text-sm text-muted-foreground">Log workouts & track progress</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/store">
            <Card className="bg-gradient-to-br from-secondary/20 to-secondary/5 border-secondary/30 hover:border-secondary transition-all cursor-pointer h-full">
              <CardContent className="pt-6">
                <Activity className="w-8 h-8 text-secondary mb-2" />
                <p className="font-heading font-bold uppercase text-lg">Gym Store</p>
                <p className="text-sm text-muted-foreground">Shop supplements & equipment</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/plans">
            <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30 hover:border-primary transition-all cursor-pointer h-full">
              <CardContent className="pt-6">
                <CreditCard className="w-8 h-8 text-primary mb-2" />
                <p className="font-heading font-bold uppercase text-lg">Upgrade Plan</p>
                <p className="text-sm text-muted-foreground">View membership options</p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Access Control */}
        {stats?.subscription_status === 'active' && (
          <Card className="bg-card/50 border-white/10 mb-8">
            <CardHeader>
              <CardTitle className="font-heading uppercase">Access Control</CardTitle>
              <CardDescription>Generate access codes to enter the gym</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="otp" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="otp" data-testid="access-tab-otp">OTP</TabsTrigger>
                  <TabsTrigger value="temp" data-testid="access-tab-temp">Temp Password</TabsTrigger>
                </TabsList>
                
                <TabsContent value="otp" className="space-y-4">
                  <div className="bg-[#1E293B] border border-white/5 shadow-[inset_0_2px_4px_rgba(0,0,0,0.3)] rounded-xl p-8 flex flex-col items-center justify-center min-h-[200px]">
                    {otp ? (
                      <div className="text-center">
                        <p className="text-5xl font-mono font-bold tracking-wider mb-2" data-testid="otp-code">{otp.otp}</p>
                        <p className="text-muted-foreground">Valid for {otp.expires_in} seconds</p>
                        <p className="text-sm text-muted-foreground mt-2">Also sent to your email</p>
                      </div>
                    ) : (
                      <Lock className="w-32 h-32 text-muted-foreground" />
                    )}
                  </div>
                  <Button
                    onClick={generateOTP}
                    disabled={loading.otp}
                    className="w-full bg-primary"
                    data-testid="generate-otp-btn"
                  >
                    {loading.otp ? 'Generating...' : 'Generate OTP'}
                  </Button>
                </TabsContent>
                
                <TabsContent value="temp" className="space-y-4">
                  <div className="bg-[#1E293B] border border-white/5 shadow-[inset_0_2px_4px_rgba(0,0,0,0.3)] rounded-xl p-8 flex flex-col items-center justify-center min-h-[200px]">
                    {tempPassword ? (
                      <div className="text-center">
                        <p className="text-4xl font-mono font-bold tracking-wider mb-2" data-testid="temp-password">{tempPassword.password}</p>
                        <p className="text-muted-foreground">Valid until {new Date(tempPassword.expires_at).toLocaleString()}</p>
                        <p className="text-sm text-muted-foreground mt-2">Also sent to your email</p>
                      </div>
                    ) : (
                      <Lock className="w-32 h-32 text-muted-foreground" />
                    )}
                  </div>
                  <Button
                    onClick={generateTempPassword}
                    disabled={loading.temp}
                    className="w-full bg-primary"
                    data-testid="generate-temp-password-btn"
                  >
                    {loading.temp ? 'Generating...' : 'Generate Temporary Password'}
                  </Button>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}

        {/* Payment History */}
        <Card className="bg-card/50 border-white/10">
          <CardHeader>
            <CardTitle className="font-heading uppercase">Payment History</CardTitle>
          </CardHeader>
          <CardContent>
            {payments.length === 0 ? (
              <p className="text-muted-foreground">No payment history</p>
            ) : (
              <div className="space-y-3">
                {payments.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex justify-between items-center border-b border-white/10 pb-3"
                    data-testid="payment-item"
                  >
                    <div>
                      <p className="font-mono">₦{payment.amount.toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded text-sm ${
                        payment.payment_status === 'paid'
                          ? 'bg-green-500/20 text-green-500'
                          : 'bg-yellow-500/20 text-yellow-500'
                      }`}
                    >
                      {payment.payment_status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MemberDashboard;
